import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, UserCog, StickyNote } from "lucide-react";
import { toast } from "sonner";

export default function DetailsPanel({ customer, onChanged }) {
  const { user } = useAuth();
  const { meta } = useMeta();
  const isManager = user.role === "admin" || user.role === "manager";

  const [form, setForm] = useState({
    company: customer.company || "", city: customer.city || "", state: customer.state || "",
    monthly_requirement: customer.monthly_requirement || "", target_price: customer.target_price || "",
    last_product: customer.last_product || "", purchase_cycle_days: customer.purchase_cycle_days || "",
    preferred_language: customer.preferred_language, customer_type: customer.customer_type,
    notes: customer.notes || "",
  });
  const [grades, setGrades] = useState(customer.tea_grades || []);
  const [blends, setBlends] = useState(customer.blend_types || []);
  const [segments, setSegments] = useState(customer.price_segments || []);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (setter, o) => setter((l) => (l.includes(o) ? l.filter((x) => x !== o) : [...l, o]));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, tea_grades: grades, blend_types: blends, price_segments: segments };
      if (body.purchase_cycle_days === "") body.purchase_cycle_days = null;
      else body.purchase_cycle_days = parseInt(body.purchase_cycle_days, 10);
      const r = await api.patch(`/customers/${customer.id}`, body);
      toast.success("Customer updated");
      onChanged?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/customers/${customer.id}/notes`, { text: note });
      toast.success("Note added");
      setNote("");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
        <h3 className="font-heading text-lg font-semibold text-stone-900 mb-4">Customer details</h3>
        <div className="grid grid-cols-2 gap-3">
          <F label="Company"><Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="details-company" /></F>
          <F label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="details-city" /></F>
          <F label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="details-state" /></F>
          <F label="Monthly requirement"><Input value={form.monthly_requirement} onChange={(e) => set("monthly_requirement", e.target.value)} placeholder="e.g. 500 kg" data-testid="details-requirement" /></F>
          <F label="Target price"><Input value={form.target_price} onChange={(e) => set("target_price", e.target.value)} placeholder="e.g. ₹200-240/kg" data-testid="details-target-price" /></F>
          <F label="Last purchased product"><Input value={form.last_product} onChange={(e) => set("last_product", e.target.value)} data-testid="details-last-product" /></F>
          <F label="Purchase cycle (days)"><Input type="number" value={form.purchase_cycle_days} onChange={(e) => set("purchase_cycle_days", e.target.value)} data-testid="details-cycle" /></F>
          <F label="Language">
            <Select value={form.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
              <SelectTrigger data-testid="details-language"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.languages || []).map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Customer type">
            <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
              <SelectTrigger data-testid="details-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="prospect">Prospect</SelectItem><SelectItem value="existing">Existing</SelectItem></SelectContent>
            </Select>
          </F>
        </div>

        <div className="mt-4 space-y-3">
          <CheckGroup title="CTC grades" options={meta?.ctc_grades || []} selected={grades} onToggle={(o) => toggleIn(setGrades, o)} testPrefix="ctc" />
          <CheckGroup title="Blended tea" options={meta?.blend_types || []} selected={blends} onToggle={(o) => toggleIn(setBlends, o)} testPrefix="blend" />
          <CheckGroup title="Price segment" options={meta?.price_segments || []} selected={segments} onToggle={(o) => toggleIn(setSegments, o)} testPrefix="segment" />
        </div>

        <div className="mt-3">
          <Label className="text-xs font-semibold text-stone-500">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5" data-testid="details-notes" />
        </div>

        <Button className="mt-4 bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={saving} data-testid="details-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save details</>}
        </Button>
      </div>

      <div className="space-y-5">
        {isManager && <ReassignCard customer={customer} onChanged={onChanged} />}
        <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3"><StickyNote className="h-4 w-4 text-amber-500" /><h3 className="font-heading font-semibold text-stone-900">Add internal note</h3></div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add an internal note (visible in the timeline)" data-testid="internal-note-input" />
          <Button className="mt-2 bg-stone-800 hover:bg-stone-700 text-white" onClick={addNote} data-testid="add-note-button">Add note</Button>
        </div>
      </div>
    </div>
  );
}

function ReassignCard({ customer, onChanged }) {
  const [target, setTarget] = useState(customer.assigned_to);
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const reassign = async () => {
    try {
      const r = await api.patch(`/customers/${customer.id}/assign`, { assigned_to: target });
      toast.success("Customer reassigned");
      onChanged?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3"><UserCog className="h-4 w-4 text-forest" /><h3 className="font-heading font-semibold text-stone-900">Assign salesperson</h3></div>
      <div className="flex gap-2">
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="flex-1" data-testid="reassign-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {users.filter((u) => u.active).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={reassign} data-testid="reassign-button">Assign</Button>
      </div>
    </div>
  );
}

function CheckGroup({ title, options, selected, onToggle, testPrefix }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-stone-500">{title}</Label>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {options.map((o) => {
          const checked = selected.includes(o);
          return (
            <label
              key={o}
              data-testid={`${testPrefix}-${o.replace(/\s+/g, "-").toLowerCase()}`}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-2 text-sm transition-colors ${
                checked ? "border-forest bg-forest-light" : "border-stone-200 hover:border-forest/40"
              }`}
            >
              <Checkbox checked={checked} onCheckedChange={() => onToggle(o)} />
              <span className={checked ? "font-medium text-forest" : "text-stone-600"}>{o}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-stone-500">{label}</Label>
      {children}
    </div>
  );
}
